import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppStore } from '@/store'
import { Loader } from '@/components/common/Loader'
import { useCatalogoCaracteristicas } from '@/hooks/useCatalogoCaracteristicas'

/** Modal para copiar las características de otra variante (de cualquier referencia).
 *  Carga el catálogo una vez (cacheado) y filtra en cliente por nombre o fórmula. */
export function CopiarCaractPicker({ currentFormula, onSelect, onClose }: {
  currentFormula: string
  onSelect: (texto: string) => void
  onClose: () => void
}) {
  const { t } = useTranslation()
  const dark = useAppStore((s) => s.dark)
  const { variantes, loading, isError, error } = useCatalogoCaracteristicas(true)
  const [q, setQ] = useState('')

  const errDetail = (() => {
    const e = error as { response?: { status?: number; data?: { detail?: string } }; message?: string } | null
    const status = e?.response?.status
    const detail = e?.response?.data?.detail ?? e?.message ?? ''
    return [status, detail].filter(Boolean).join(' · ')
  })()

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const filtradas = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return variantes
      .filter((v) => v.formula !== currentFormula && v.texto.trim().length > 0)
      .filter((v) => !needle || `${v.nombreProducto} ${v.formula}`.toLowerCase().includes(needle))
  }, [variantes, q, currentFormula])

  const panel = dark ? 'bg-slate-900 border-white/10 text-slate-200' : 'bg-white border-slate-200 text-slate-700'
  const muted = dark ? 'text-slate-400' : 'text-slate-500'
  const inputCls = `w-full text-[13px] px-3 py-2 rounded-lg border outline-none ${dark ? 'bg-white/5 border-white/10 text-slate-100 placeholder-slate-500' : 'bg-white border-slate-300 text-slate-800 placeholder-slate-400'}`
  const rowCls = `w-full text-left px-3 py-2.5 rounded-lg border transition-colors cursor-pointer ${dark ? 'border-white/8 hover:bg-white/5' : 'border-slate-200 hover:bg-slate-50'}`

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div className={`w-full max-w-lg rounded-2xl border shadow-2xl p-5 flex flex-col max-h-[80vh] ${panel}`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-[15px] font-semibold">{t('bomCaptura.copiar.titulo')}</h2>
          <button onClick={onClose} className={`shrink-0 ${muted} hover:opacity-70`} aria-label="cerrar">✕</button>
        </div>

        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('bomCaptura.copiar.buscar')}
          className={`${inputCls} mt-3`}
        />

        <div className="mt-3 flex-1 min-h-0 overflow-y-auto space-y-1.5">
          {loading ? (
            <div className="py-8"><Loader /></div>
          ) : isError ? (
            <div className="py-8 text-center">
              <p className="text-[13px] text-red-500">{t('bomCaptura.copiar.error')}</p>
              {errDetail && <p className={`mt-1 text-[11px] ${muted}`}>{errDetail}</p>}
            </div>
          ) : filtradas.length === 0 ? (
            <p className={`text-[13px] text-center py-8 ${muted}`}>{t('bomCaptura.copiar.vacio')}</p>
          ) : (
            filtradas.map((v) => (
              <button
                key={v.formula}
                type="button"
                className={rowCls}
                onClick={() => { onSelect(v.texto); onClose() }}
              >
                <div className="text-[13px] font-semibold">{v.formula}</div>
                {v.nombreProducto && <div className={`text-[12px] ${muted}`}>{v.nombreProducto}</div>}
                {v.preview && <div className={`text-[11px] truncate ${muted}`}>{v.preview}</div>}
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  )
}
